import { DecisionTreeType } from '@provider-directory/contexts/DecisionTreeContext';
import {
  DecisionTreeQuestionOption,
  SceneAction,
} from '@provider-directory/models/decision-tree.model';

export interface ActionContext {
  decisionTree: DecisionTreeType;
  selectedOption: DecisionTreeQuestionOption;
  updateDecisionTree: (patch: DecisionTreeType) => DecisionTreeType;
}

/**
 * Mapping of base RFV keys → full RFV IDs with age suffix
 * It needs to be done because the different format of age groups is used in provided the RFV file
 */
const ageSuffixMap: Record<
  string,
  {
    between18and49?: string;
    over50?: string;
    between18and35?: string;
    over36?: string;
  }
> = {
  'calf-pain': {
    between18and49: 'calf-pain-18-49-yo',
    over50: 'calf-pain-50-and-over',
  },
  'hamstring-or-quadriceps-injury': {
    between18and49: 'hamstring-or-quadriceps-injury-18-49-yo',
    over50: 'hamstring-or-quadriceps-injury-50-and-over',
  },
  'hip-issue': {
    between18and49: 'hip-issue-18-49-yo',
    over50: 'hip-issue-50-and-over',
  },
  'knee-pain': {
    between18and49: 'knee-pain-18-49-yo',
    over50: 'knee-pain-50-and-over',
  },
  'leg-fracture': {
    between18and49: 'leg-fracture-18-49-yo',
    over50: 'leg-fracture-50-and-over',
  },
  'shoulder-arthritis': {
    between18and49: 'shoulder-arthritis-18-49',
    over50: 'shoulder-arthritis-50-and-over',
  },
  'something-else-knee': {
    between18and49: 'something-else-knee-18-49-yo',
    over50: 'something-else-knee-50-and-over',
  },
  'something-else-shoulder': {
    between18and49: 'something-else-shoulder-18-49',
    over50: 'something-else-shoulder-50-and-over',
  },
  'type-1-diabetes': {
    between18and35: 'type-1-diabetes-18-to-35-yo',
    over36: 'type-1-diabetes-36-and-older',
  },
};

/**
 * Get RFV with age suffix according to mapping table.
 */
type AgeGroup = 'between18and49' | 'over50';
const withAgeSuffix = (rfv: string, ageGroup: AgeGroup | undefined): string => {
  if (!ageGroup) return rfv;

  const mapping = ageSuffixMap[rfv];
  if (mapping && mapping[ageGroup]) {
    return mapping[ageGroup] ?? rfv;
  }

  // fallback — no age suffix defined for this RFV
  return rfv;
};

/**
 * Append Medicaid flag
 * - "medicaid" → `-medicaid`
 * - "no" → `-no-medicaid`
 */
const appendMedicaidFlag = (rfv: string, selectedValue: string): string => {
  if (selectedValue === 'medicaid') {
    return `${rfv}-medicaid`;
  }
  if (selectedValue === 'no') {
    return `${rfv}-no-medicaid`;
  }
  return rfv;
};

type DecisionTreeAction = (context: ActionContext) => DecisionTreeType;

type DecisionTreeActionsMap = Record<SceneAction, DecisionTreeAction>;

const decisionTreeActionsMap: DecisionTreeActionsMap = {
  rfvAddAgeGroup: ({ decisionTree, updateDecisionTree }) => {
    const baseRFV = decisionTree.rfvKey || '';
    const ageGroup = decisionTree['is-adult'] as AgeGroup;
    const rfvId = withAgeSuffix(baseRFV, ageGroup);

    return updateDecisionTree({ ...decisionTree, rfvId });
  },

  rfvAppendValue: ({ decisionTree, selectedOption, updateDecisionTree }) => {
    const baseRFV = decisionTree.rfvKey || '';
    const rfvId = appendMedicaidFlag(baseRFV, selectedOption.value);

    return updateDecisionTree({ ...decisionTree, rfvId });
  },

  setRfv: ({ decisionTree, selectedOption, updateDecisionTree }) => {
    const updatedRfv = {
      rfvKey: selectedOption.value,
      rfvId: selectedOption.value,
    };
    return updateDecisionTree({ ...decisionTree, ...updatedRfv });
  },
};

export const getSceneActionByType = (type: SceneAction): DecisionTreeAction =>
  decisionTreeActionsMap[type];
